import type {
  AmendmentField,
  OperationsSettingsView,
  DisposalMethod,
  FoundItem,
  FoundItemSummary,
  Incident,
  IncidentPersonRole,
  IncidentReportOptions,
  IncidentSeverity,
  IncidentStatus,
  IncidentSummary,
  IncidentType,
  InjuryTreatment,
  LostFoundCategory,
  LostReport,
  PersonIncidentLink,
  InspectionOutcome,
  InspectionRun,
  InspectionRunSummary,
  InspectionTemplate,
  OperationsRunResult,
  SetupTemplate,
  WorkOrder,
  WorkOrderStatus,
  WorkOrderSummary,
  Asset,
  AssetCategory,
  AssetCheckout,
  AssetCondition,
  AssetSummary,
  MaintenanceSchedule,
  ReplacementYear,
  EventPlace,
  Facility,
  FacilitySummary,
  PlaceEvent,
  Space,
  SpaceSummary,
  NoteSubjectType,
  ThemePreference,
  FormDefinition,
  FormDetail,
  FormRouting,
  FormSummary,
  SubmissionDetail,
  SubmissionStatus,
  SubmissionSummary,
  AccessGrant,
  Event360,
  EventSettlement,
  DealTreatment,
  SettlementDealTerms,
  SettlementPhase,
  SettlementSourceFigure,
  SettlementSourcePull,
  SettlementTotals,
  SourceComparison,
  DealPnl,
  DealStatement,
  SettlementStage,
  GrantHolder,
  PositionAccessView,
  PositionRuleBody,
  PositionRunResult,
  PendingPreload,
  PreloadReport,
  PreloadRequest,
  FinanceAccount,
  FinanceException,
  FinanceOverviewApp,
  FinancePayoutRow,
  FinancePostingRow,
  FinanceProfile,
  FinanceSync,
  HubLookupResponse,
  OrganizationSourcesResponse,
  ExternalReferenceHistory,
  OrganizationLinkCandidatesResponse,
  OrganizationLinkPreview,
  OrganizationLinkSource,
  LauncherApp,
  MembershipSummary,
  Note,
  NoteVersion,
  OrganizationRelationship,
  ExternalReference,
  OrganizationSummary,
  Person,
  AccountEmailStatus,
  AccountEmailChangeResult,
  AccountIdentities,
  IdentityChangeStarted,
  PreloadInviteStep,
  PersonHistory,
  PersonSummary,
  PersonWork,
  Principal,
  DuplicateGroup,
  MergeCase,
  PeopleFilter,
  PeopleList,
  PeopleSyncResult,
  PeopleSyncStatus,
  MatchSuggestion,
  OrganizationFilter,
  OrganizationList,
  OrganizationMerge,
  OrganizationOrigin,
  OrganizationSourceRecord,
  OrgImportResult,
  OrgImportStatus,
  SourceEventList,
  SourceHealth,
  Sourced,
} from '@bre-hub/contracts'

export type SettlementLineInput = {
  category: string
  label: string
  amountCents: number
  note?: string | null
  dealTreatment?: DealTreatment
  receipt?: { documentId: string; version: number } | null
  expenseCategory?: { source: 'booking' | 'hub'; ref: string } | null
  dealPartyKey?: string | null
}

// The night's money, live (ADR 0039): what every linked app says now.
export type NightLine = SettlementSourceFigure & { sourceService: string; sourceId: string; sourceTitle: string; asOf: string; adminUrl: string | null }
export type NightPerformance = {
  sourceId: string
  sourceTitle: string
  adminUrl: string | null
  asOf: string
  id: string
  startsAt: string
  status: string
  capacity: number | null
  ticketsSold: number
  doorCents: number
  feesCents: number | null
  compCount: number | null
  checkIns: number | null
  refundsCents: number | null
  accountingMode: string | null
}
export type NightPosRow = { name: string; orderCount: number; chargedCents: number; salesCents: number | null; refundsCents: number | null; tipsCents: number | null }
export type NightMoney = {
  asOf: string
  pulls: SettlementSourcePull[]
  lines: NightLine[]
  totals: SettlementTotals
  incomplete: string[]
  warnings: string[]
  performances: NightPerformance[]
  pos: { sourceId: string; title: string; asOf: string; adminUrl: string | null; locations: NightPosRow[]; registers: NightPosRow[] }[]
}
export type SettlementListItem = {
  id: string
  eventId: string
  eventName: string
  eventStartsOn: string
  version: number
  stage: SettlementStage
  phase: SettlementPhase
  phaseLabel: string
  counterpartyName: string | null
  amountDueCents: number | null
  parties?: { name: string; amountDueCents: number | null }[]
  complete: boolean
  updatedAt: string
}
export type ExpenseCatalog = {
  hub: { ref: string; label: string; category: string }[]
  booking: { status: string; asOf: string; reason?: string; categories: { sourceId: string; label: string; sortOrder: number; active: boolean }[] }
}
export type SettlementPartyInput = Omit<SettlementDealTerms, 'organizationId'> & { organizationId?: string | null }
export type SettlementStepInput =
  | { kind: 'sent'; partyKey?: string | null; sentTo: string; sentVia: 'email' | 'in_person' | 'mail' | 'other'; note?: string | null }
  | { kind: 'countersigned'; partyKey?: string | null; signerName: string; signerTitle?: string | null; note?: string | null }
  | { kind: 'payment'; partyKey?: string | null; paymentDirection: 'bre_to_counterparty' | 'counterparty_to_bre'; amountCents: number; method: string; reference?: string | null; paidOn: string; note?: string | null }
  | { kind: 'write_off'; partyKey?: string | null; paymentDirection: 'bre_to_counterparty' | 'counterparty_to_bre'; amountCents: number; note: string }
  | { kind: 'closed'; note?: string | null }
  | { kind: 'reopened'; note: string }

// Hub's finance shadow for an event's dates and linked apps (ADR 0010).
export type EventFinance = {
  window: { from: string; to: string; payoutsTo: string }
  apps: { app: string; lastStatus: string | null; lastSuccessAt: string | null; lastAttemptAt: string | null; windowFrom: string | null; windowTo: string | null }[]
  postings: FinancePostingRow[]
  postingsTruncated: boolean
  payouts: FinancePayoutRow[]
  payoutsTruncated: boolean
}

export type TargetType = 'project' | 'event' | 'person' | 'organization' | 'case' | 'facility' | 'space' | 'asset' | 'work_order' | 'inspection_run' | 'grant' | 'purchase_request' | 'none'

// A space as pickers list it: no layouts.
export type SpaceOption = Omit<SpaceSummary, 'configurations' | 'maxCapacity'>
type FacilityInput = { name: string; kind: string; status?: 'active' | 'inactive'; address?: string | null; notes?: string | null }
type SpaceInput = { name: string; kind: string; status?: 'active' | 'inactive'; parentSpaceId?: string | null; notes?: string | null }
export type SearchGroupType = 'person' | 'organization' | 'event' | 'project' | 'case' | 'task' | 'facility' | 'space' | 'asset' | 'work_order' | 'found_item' | 'workforce' | 'meeting' | 'resolution' | 'policy' | 'record'
export type WorkOrderInput = {
  title: string
  description?: string | null
  type: string
  priority?: string
  facilityId?: string | null
  spaceId?: string | null
  assetId?: string | null
  eventId?: string | null
  requesterPersonId?: string | null
  assigneePersonId?: string | null
  dueOn?: string | null
  sourceReference?: string | null
  vendorOrganizationId?: string | null
  inspectionRunId?: string
  inspectionItemKey?: string
}
export type WorkOrderChange = Partial<WorkOrderInput> & { status?: WorkOrderStatus; completionNotes?: string | null; laborMinutes?: number | null; partsNote?: string | null; partsCostCents?: number | null; expectedVersion: number }
export type SetupTemplateInput = { kind: 'setup' | 'reset'; title: string; description?: string | null; spaceId?: string | null; offsetDays: number; priority: string }
export type EventSetupTemplate = SetupTemplate & { dueOn: string; workOrder: { id: string; number: number; status: WorkOrderStatus } | null }
export type InspectionTemplateInput = {
  name: string
  subjectType: 'facility' | 'space' | 'asset_category'
  subjectId: string
  items: { key?: string; label: string; help?: string | null }[]
  recurrenceDays?: number | null
  startsOn?: string | null
  assigneePersonId?: string | null
}
export type AssetInput = {
  name: string
  tag: string
  categoryId: string
  status?: 'in_service' | 'out_for_repair' | 'retired' | 'lost'
  condition?: AssetCondition | null
  facilityId?: string | null
  spaceId?: string | null
  serialNumber?: string | null
  make?: string | null
  model?: string | null
  purchaseDate?: string | null
  purchaseCostCents?: number | null
  vendorOrganizationId?: string | null
  warrantyExpiresOn?: string | null
  replacementYear?: number | null
  replacementCostCents?: number | null
  notes?: string | null
}
export type MaintenanceScheduleInput = {
  title: string
  instructions?: string | null
  assetId?: string | null
  categoryId?: string | null
  intervalValue?: number | null
  intervalUnit?: 'days' | 'months' | null
  startsOn?: string | null
  fixedDates?: string[]
  leadDays: number
  assigneePersonId?: string | null
}

export type HubTask = {
  sourceRecord?: { kind: 'rental' | 'sponsorship' | 'show' | 'general'; id: string | null; role: string | null } | null
  id: string
  title: string
  description: string | null
  status: 'open' | 'in_progress' | 'blocked' | 'done' | 'cancelled'
  priority: 'low' | 'normal' | 'high' | 'urgent'
  dueOn: string | null
  assigneePersonId: string | null
  assigneeName: string | null
  createdByPersonId: string | null
  creatorName: string | null
  targetType: Exclude<TargetType, 'case' | 'asset' | 'work_order' | 'inspection_run' | 'purchase_request'>
  targetId: string | null
  targetLabel: string | null
  milestoneId: string | null
  requirementId: string | null
  requirementTitle: string | null
  incidentId: string | null
  completedAt: string | null
  createdAt: string
  updatedAt: string
}

export type HubProject = {
  id: string
  name: string
  kind: 'event_production' | 'capital' | 'grant' | 'marketing' | 'operations' | 'other'
  status: 'proposed' | 'active' | 'on_hold' | 'completed' | 'cancelled'
  ownerPersonId: string | null
  ownerName: string | null
  startsOn: string | null
  dueOn: string | null
  summary: string | null
  budgetCents: number | null
  createdAt: string
  updatedAt: string
  completedAt: string | null
  openTasks: number
  overdueTasks: number
}

export type HubProjectDetail = HubProject & {
  members: { personId: string; name: string; role: string; addedAt: string }[]
  events: { id: string; name: string; startsOn: string; endsOn: string; status: string }[]
  milestones: { id: string; title: string; dueOn: string | null; completedAt: string | null; taskCount: number; doneCount: number }[]
}

export type HubDocument = {
  id: string
  title: string
  classification: 'public' | 'internal' | 'confidential'
  kind: 'file' | 'link'
  url: string | null
  contentType: string | null
  sizeBytes: number | null
  sha256: string | null
  targetType: TargetType
  targetId: string | null
  addedByName: string | null
  createdAt: string
  retiredAt: string | null
  currentVersion: number | null
}

export type HubDocumentVersion = {
  version: number
  filename: string | null
  contentType: string | null
  sizeBytes: number | null
  sha256: string
  uploadedByPersonId: string | null
  uploadedByName: string | null
  createdAt: string
}

export type ApprovalSubjectType = 'document' | 'project' | 'request' | 'settlement' | 'grant_submission' | 'purchase_request' | 'campaign' | 'booking_money'
export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'invalidated'

// One document as frozen into a snapshot. The storage key stays in core.
export type SnapshotDocument = {
  documentId: string
  version: number
  title: string
  classification: string
  filename: string | null
  contentType: string | null
  sizeBytes: number | null
  sha256: string
}

export type ApprovalSnapshot =
  | ({ subjectType: 'document' } & SnapshotDocument)
  | {
      // A refund, or a discount over the threshold, on a rental or
      // sponsorship (ADR 0045): what is asked and what Booking said then.
      subjectType: 'booking_money'
      kind: 'refund' | 'discount'
      record: { kind: 'rental' | 'sponsorship'; id: string; title: string | null }
      invoice: { id: string; number: string | null } | null
      paymentId: string | null
      method: string | null
      amountCents: number
      currency: string
      reason: string
      discount: { note: string | null; depositCents: number | null; depositDueDate: string | null; subtotalCents: number | null; percent: number } | null
      requestKey: string
      booking: import('@bre-hub/contracts').BookingMoneySnapshot
      requestedByPersonId: string
    }
  | {
      // A campaign (ADR 0035): what goes and to whom, frozen.
      subjectType: 'campaign'
      campaignId: string
      name: string
      brandKey: string
      purposeKey: string
      channel: string
      templateVersionId: string
      templateContentHash: string
      templateName: string | null
      templateVersion: number | null
      subject: string
      preheader: string | null
      segmentName: string | null
      eventName: string | null
      eventDate: string | null
      definition: import('@bre-hub/contracts').SegmentDefinition
      audienceAsOf: string
      audienceIncluded: number
      audienceExcluded: Record<string, number>
      // A text (ADR 0036): the rendered sample, the estimate and the budget.
      urgent?: boolean
      urgentReason?: string | null
      smsSample?: string | null
      smsSegments?: number
      smsCostMicros?: number
      smsSegmentPriceMicros?: number
      smsBudget?: { month: string; spentMicros: number; budgetMicros: number | null; remainingMicros: number | null; hardStop: boolean; overBudget: boolean }
      // A push (ADR 0037): the notification with a sample first name.
      pushSample?: { title: string; body: string; url: string | null }
    }
  | import('./pages/CommsJourneys').JourneySnapshotData
  | {
      subjectType: 'project'
      projectId: string
      name: string
      summary: string | null
      budgetCents: number | null
      currency: string
      startsOn: string | null
      dueOn: string | null
      ownerPersonId: string | null
      ownerName: string | null
    }
  | { subjectType: 'request'; title: string; description: string | null; amountCents: number | null; currency: string | null; documents: SnapshotDocument[] }
  | {
      subjectType: 'grant_submission'
      grantId: string
      programName: string
      description: string | null
      funderOrganizationId: string
      funderName: string
      ownerPersonId: string | null
      ownerName: string | null
      amountRequestedCents: number | null
      currency: string
      submissionDeadline: string | null
      awardStartsOn: string | null
      awardEndsOn: string | null
      restriction: 'unrestricted' | 'restricted'
      restrictionPurpose: string | null
      matchRequired: boolean
      matchKind: 'amount' | 'percent' | null
      matchAmountCents: number | null
      matchPercent: number | null
      matchDescription: string | null
      statusWhenAsked: string
    }
  | {
      subjectType: 'purchase_request'
      purchaseRequestId: string
      number: number
      label: string
      title: string
      description: string | null
      requesterName: string | null
      vendorName: string | null
      vendorOrganizationId: string | null
      newVendor: boolean
      departmentName: string | null
      neededBy: string | null
      lines: { description: string; quantity: number; unitCostCents: number; totalCents: number }[]
      totalCents: number
      currency: string
      quotes: SnapshotDocument[]
    }
  | ({ subjectType: 'settlement'; eventName: string; eventStartsOn: string; eventEndsOn: string; signConvention: number } & Pick<
      EventSettlement,
      'eventId' | 'version' | 'revisesSettlementId' | 'stage' | 'currency' | 'note' | 'sources' | 'sourcesPulledAt' | 'totals'
    > & {
        settlementId: string
        lines: { lineId: string; position: number; origin: 'source' | 'manual'; category: EventSettlement['lines'][number]['category']; label: string; amountCents: number; note: string | null; source: EventSettlement['lines'][number]['source']; dealTreatment?: DealTreatment }[]
        deal?: SettlementDealTerms
        statement?: DealStatement
        // ADR 0040: with several parties, every one's terms and statement.
        statements?: { key: string; position: number; counterpartyName: string; terms: SettlementDealTerms; statement: DealStatement }[]
        dealPnl?: DealPnl
        incomplete?: string[]
      })

export type ApprovalSummary = {
  id: string
  subjectType: ApprovalSubjectType
  subjectId: string | null
  subjectVersion: number | null
  title: string
  status: ApprovalStatus
  statusReason: string | null
  rule: 'all' | 'any'
  dueOn: string | null
  requestedBy: { personId: string; name: string | null }
  createdAt: string
  closedAt: string | null
  snapshotHash: string
  approvers: { personId: string; name: string | null; decision: 'approve' | 'reject' | null; decidedAt: string | null }[]
  subjectChanged: boolean
  waitingOnMe: boolean
}

export type ApprovalDetail = ApprovalSummary & {
  note: string | null
  snapshot: ApprovalSnapshot
  decisions: { id: string; approverPersonId: string; approverName: string | null; decision: 'approve' | 'reject'; comment: string | null; snapshotHash: string; decidedAt: string }[]
  current: { version: number | null; retired: boolean; title: string | null; classification: string | null } | null
  linkedDocuments: { documentId: string; version: number; currentVersion: number | null; retired: boolean }[]
  canDecide: boolean
  canWithdraw: boolean
  activity: { id: string; occurredAt: string; actorName: string | null; action: string; detail: unknown }[]
}

export type ApprovalSubjectInput =
  | { subjectType: 'document'; subjectId: string; documentVersion?: number }
  | { subjectType: 'project'; subjectId: string }
  | { subjectType: 'settlement'; subjectId: string }
  | { subjectType: 'grant_submission'; subjectId: string }
  | { subjectType: 'purchase_request'; subjectId: string }
  | { subjectType: 'campaign'; subjectId: string }
  | { subjectType: 'journey'; subjectId: string }
  | { subjectType: 'request'; title: string; description?: string; amountCents?: number; documents?: { documentId: string; version: number }[] }

export type RequirementSubjectType = 'project' | 'organization' | 'person' | 'event' | 'facility' | 'space' | 'grant'
export type RequirementKind = 'document' | 'approval' | 'payment' | 'form' | 'source_state' | 'other'
export type RequirementStatus = 'open' | 'satisfied' | 'waived' | 'not_applicable' | 'expired'
export type EvidenceKind = 'document' | 'approval' | 'source_reference'

export type RequirementEvidence = {
  id: string
  kind: EvidenceKind | 'waiver'
  cycle: number
  recordedAt: string
  recordedByPersonId: string
  recordedByName: string | null
  note: string | null
  reason: string | null
  validUntil: string | null
  // A document or approval the viewer can't open shows only that it exists.
  document: null | { visible: false } | { visible: true; documentId: string; title: string; classification: string; version: number; sha256: string; currentVersion: number | null }
  approval: null | { visible: false } | { visible: true; approvalId: string; title: string; snapshotHash: string; subjectChanged: boolean }
  source: null | { service: string; entity: string; id: string; verified: false }
}

export type HubRequirement = {
  id: string
  subjectType: RequirementSubjectType
  subjectId: string
  subjectLabel: string | null
  title: string
  description: string | null
  kind: RequirementKind
  dueOn: string | null
  ownerPersonId: string | null
  ownerName: string | null
  status: RequirementStatus
  statusReason: string | null
  renewsEveryMonths: number | null
  expiresOn: string | null
  cycle: number
  templateId: string | null
  templateVersion: number | null
  templateName: string | null
  createdAt: string
  updatedAt: string
  version: number
  overdue: boolean
  dueSoon: boolean
  expiringSoon: boolean
  currentEvidence: RequirementEvidence | null
  acceptsEvidence: EvidenceKind[]
  canSatisfy: boolean
  canManage: boolean
  canWaive: boolean
}

export type HubRequirementDetail = HubRequirement & {
  evidence: RequirementEvidence[]
  events: { id: string; action: string; fromStatus: string | null; toStatus: string | null; evidenceId: string | null; reason: string | null; detail: unknown; actorName: string | null; occurredAt: string }[]
  tasks: { id: string; title: string; status: HubTask['status']; dueOn: string | null; assigneeName: string | null }[]
}

export type SatisfyEvidence =
  | { kind: 'document'; documentId: string; version: number; expectedSha256?: string }
  | { kind: 'approval'; approvalId: string }
  | { kind: 'source_reference'; sourceService: string; sourceEntity: string; sourceId: string }

export type ChecklistItem = { key?: string; title: string; description: string | null; kind: RequirementKind; dueOffsetDays: number | null; renewsEveryMonths: number | null }
export type ChecklistVersion = { version: number; name: string; description: string | null; items: ChecklistItem[]; createdByName: string | null; createdAt: string }
export type HubChecklist = {
  id: string
  name: string
  currentVersion: number
  createdAt: string
  updatedAt: string
  retiredAt: string | null
  current: ChecklistVersion
  versions?: ChecklistVersion[]
  stamps?: { id: string; version: number; subjectType: RequirementSubjectType; subjectId: string; subjectLabel: string | null; anchorOn: string; stampedAt: string; stampedByName: string | null }[]
}

export type ReminderItemType = 'requirement' | 'task' | 'approval'
export type ReminderRule = {
  id: string
  itemType: ReminderItemType
  itemId: string | null
  anchor: 'due' | 'expiry'
  offsetDays: number
  action: 'remind' | 'escalate'
  escalateToPersonId: string | null
  escalateToName: string | null
  // Also emailed (ADR 0008).
  email: boolean
  isDefault: boolean
  createdAt: string
  retiredAt: string | null
}
// null: sent before reminder emails existed.
export type ReminderEmailStatus = 'pending' | 'sending' | 'sent' | 'simulated' | 'skipped' | 'failed'
export type NotificationSettings = { emailReminders: boolean; emailAddress: string | null }
export type ReminderView = {
  rules: ReminderRule[]
  sends: { id: string; ruleId: string; cycleKey: string; anchorOn: string; offsetDays: number; action: 'remind' | 'escalate'; recipientName: string | null; emailStatus: ReminderEmailStatus | null; sentAt: string }[]
  upcoming: { ruleId: string; on: string; anchor: 'due' | 'expiry'; anchorOn: string; offsetDays: number; action: 'remind' | 'escalate'; escalateToName: string | null }[]
  canSetRules: boolean
  today: string
}

export type HubNotification = { id: string; kind: string; title: string; body: string | null; link: string | null; createdAt: string; readAt: string | null }

export type HubEvent = {
  id: string
  name: string
  startsOn: string
  endsOn: string
  status: 'planned' | 'confirmed' | 'completed' | 'cancelled'
  notes: string | null
  createdAt: string
  updatedAt: string
  links: { id: string; sourceService: string; entityType: string; sourceId: string; titleSnapshot: string; startsAtSnapshot: string | null; linkedAt: string }[]
  series: { id: string; name: string } | null
  kind: 'show' | 'festival' | 'rental' | 'community' | 'other' | null
  origin: 'hub' | 'bre-ticketing' | 'booking' | 'series'
  serviceStartsLocal: string | null
  serviceEndsLocal: string | null
  review: 'possible_duplicate' | 'source_canceled' | 'dates_widened' | null
  version: number
  isParent: boolean
  parent: { id: string; name: string } | null
  noSettlement: { at: string; reason: string | null } | null
}

export type CaseChannel = 'phone' | 'email' | 'walk_in' | 'web' | 'other'
export type CaseQueue = 'box_office' | 'rentals' | 'sponsorship' | 'rewards' | 'finance' | 'technical' | 'general'
export type CaseStatus = 'new' | 'open' | 'waiting_on_customer' | 'resolved' | 'closed'
export type CasePriority = 'low' | 'normal' | 'high' | 'urgent'

export type SupportCaseSummary = {
  id: string
  number: number
  subject: string
  requesterPersonId: string | null
  requesterName: string | null
  organizationId: string | null
  organizationName: string | null
  channel: CaseChannel
  queue: CaseQueue
  status: CaseStatus
  priority: CasePriority
  assigneePersonId: string | null
  assigneeName: string | null
  dueAt: string | null
  category: string | null
  createdAt: string
  updatedAt: string
  resolvedAt: string | null
  closedAt: string | null
}

export type CaseEntry = {
  id: string
  kind: 'internal_note' | 'customer_message' | 'status_change' | 'assignment'
  body: string
  authorPersonId: string | null
  authorName: string | null
  createdAt: string
}

export type CaseLink = { id: string; targetType: 'person' | 'organization' | 'event' | 'external'; targetId: string; label: string; createdAt: string }

export type SupportCase = SupportCaseSummary & { createdByPersonId: string | null; createdByName: string | null; entries: CaseEntry[]; links: CaseLink[] }

export type CaseUpdate = Partial<{
  subject: string
  status: CaseStatus
  priority: CasePriority
  queue: CaseQueue
  assigneePersonId: string | null
  requesterPersonId: string | null
  organizationId: string | null
  category: string
}>

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

// A 401 means the one-hour session ended (or Auth signed out). Going back
// through sign-in is silent while the BRE Auth session is alive, and lands
// on the same page.
export function reauthenticate(): never {
  const returnTo = `${location.pathname}${location.search}`
  location.assign(`/auth/login?returnTo=${encodeURIComponent(returnTo)}`)
  throw new ApiError(401, 'SIGN_IN_REQUIRED', 'Signing in again')
}

export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? { accept: 'application/json' } : { accept: 'application/json', 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (response.status === 401) reauthenticate()
  const payload = (await response.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null
  if (!response.ok) {
    throw new ApiError(response.status, payload?.error?.code ?? 'ERROR', payload?.error?.message ?? `Request failed (${response.status})`)
  }
  return payload as T
}

export type Me = Principal & {
  sessionExpiresAt: string
  environment: 'production' | 'development'
  commands: string[]
  appLinks: { bookingUrl: string | null; ticketingAdminUrl: string | null }
  // Whether pre-loading rewards works in this Hub, and if not, why; and
  // which parts (drink tickets, points) are switched on.
  preload: { available: boolean; reason: string | null; kinds: { tickets: boolean; points: boolean } }
  // Whether reminder emails really go out in this Hub (ADR 0008).
  emailDelivery: 'live' | 'simulated' | 'unconfigured'
  // Public forms (ADR 0011).
  forms: { publicOrigin: string | null; personLinking: { available: boolean; reason: string | null }; turnstile: boolean }
  // Call pop (ADR 0038): whether this Hub runs the live channel. Absent on an older Worker.
  callPop?: boolean
  // Booking commands (rentals and sponsorships plan, Phase 2): whether this Hub sends them. Absent on an older Worker.
  bookingCommands?: { enabled: boolean; reason: string | null; money?: { enabled: boolean; reason: string | null } }
}

export type FormTemplateSummary = { key: string; name: string; description: string; slug: string; fields: number }
export type FormSettingsChange = {
  expectedSettingsVersion: number
  name?: string
  slug?: string
  opensAt?: string | null
  closesAt?: string | null
  submissionCap?: number | null
  sendConfirmation?: boolean
  retentionDays?: number | null
  routing?: FormRouting
}
export type SubmissionListParams = { formId?: string; status?: SubmissionStatus | ''; personId?: string; from?: string; to?: string; offset?: number }

export type CommandOutcome<R> =
  | { outcome: 'applied' | 'duplicate'; result: R }
  | { outcome: 'refused'; code: string; message: string; current?: unknown }
  | { outcome: 'unknown' | 'not_configured'; message: string }
export type OrganizationDetail = OrganizationSummary & { createdAt: string; updatedAt: string; memberships: MembershipSummary[]; externalReferences: ExternalReference[] }
export type ActivityEntry = { id: string; occurredAt: string; actorName: string | null; action: string; detail: unknown }

export type FinanceAccountEntry = { app: string; label: string; reports: boolean; registry: FinanceAccount | null; reported: FinanceProfile | null; reportedAt: string | null }
export type FinanceRefreshResult = { app: string; status: 'ok' | 'error' | 'not_connected'; error?: string; postings?: number; payouts?: number; issues?: number }
export const qs = (params: Record<string, string | undefined>) => new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString()

async function upload(path: string, form: FormData): Promise<{ document: HubDocument }> {
  const response = await fetch(path, { method: 'POST', credentials: 'same-origin', body: form })
  if (response.status === 401) reauthenticate()
  const payload = (await response.json().catch(() => null)) as { document?: HubDocument; error?: { code?: string; message?: string } } | null
  if (!response.ok) throw new ApiError(response.status, payload?.error?.code ?? 'ERROR', payload?.error?.message ?? `Upload failed (${response.status})`)
  return payload as { document: HubDocument }
}

// Incidents and lost and found (ADR 0015).
export type IncidentPersonInput = { personId?: string | null; name?: string | null; role: IncidentPersonRole; detail?: string | null }
export type IncidentReportInput = {
  occurredLocal: string
  type: IncidentType
  narrative: string
  severity?: IncidentSeverity
  facilityId?: string | null
  spaceId?: string | null
  locationDetail?: string | null
  eventId?: string | null
  immediateActions?: string | null
  onBehalfOf?: string | null
  people?: IncidentPersonInput[]
  emsCalled?: boolean
  policeCalled?: boolean
  agencyReference?: string | null
  injuryBodyPart?: string | null
  injuryTreatment?: InjuryTreatment | null
  confidential?: boolean
}
export type IncidentChange = Partial<{
  status: Exclude<IncidentStatus, 'closed'>
  severity: IncidentSeverity
  facilityId: string | null
  spaceId: string | null
  locationDetail: string | null
  eventId: string | null
  assetId: string | null
  emsCalled: boolean
  policeCalled: boolean
  agencyReference: string | null
  injuryBodyPart: string | null
  injuryTreatment: InjuryTreatment | null
  confidential: boolean
  investigatorPersonId: string | null
  rootCause: string | null
  correctiveAction: string | null
}> & { expectedVersion: number }
export type AmendmentInput = { field: AmendmentField; newValue?: string | null; person?: IncidentPersonInput; personRowId?: string; reason: string }
export type IncidentListParams = { scope?: 'all' | 'mine'; status?: string; type?: string; severity?: string; facilityId?: string; eventId?: string; from?: string; to?: string; overdue?: string; q?: string }
export type FoundItemInput = {
  foundLocal: string
  category: LostFoundCategory
  description: string
  highValue?: boolean
  contentsNote?: string | null
  contentsWitnessPersonId?: string | null
  facilityId?: string | null
  spaceId?: string | null
  locationDetail?: string | null
  eventId?: string | null
  storageLocation?: string | null
  foundByPersonId?: string | null
  foundByName?: string | null
}
export type ClaimInput = { claimantName: string; claimantContact?: string | null; claimantPersonId?: string | null; claimantDescription: string; release?: boolean; verificationNote?: string | null; signatureAck?: boolean }
export type LostReportInput = { lostOn: string; category: LostFoundCategory; description: string; guestName: string; guestContact?: string | null; guestPersonId?: string | null; facilityId?: string | null; spaceId?: string | null; locationDetail?: string | null; eventId?: string | null }

async function uploadOwnFile(path: string, file: File): Promise<{ fileId: string }> {
  const form = new FormData()
  form.set('file', file)
  const response = await fetch(path, { method: 'POST', credentials: 'same-origin', body: form })
  if (response.status === 401) reauthenticate()
  const payload = (await response.json().catch(() => null)) as { fileId?: string; error?: { code?: string; message?: string } } | null
  if (!response.ok) throw new ApiError(response.status, payload?.error?.code ?? 'ERROR', payload?.error?.message ?? `Upload failed (${response.status})`)
  return payload as { fileId: string }
}

export const api = {
  me: () => request<Me>('GET', '/api/me'),
  launcher: () => request<{ apps: LauncherApp[] }>('GET', '/api/launcher'),
  people: (q: string) => request<{ people: PersonSummary[] }>('GET', `/api/people?q=${encodeURIComponent(q)}`),
  peopleList: (q: string, filter: PeopleFilter, offset: number) =>
    request<PeopleList>('GET', `/api/people?${qs({ q, filter, offset: offset > 0 ? String(offset) : undefined })}`),
  peopleSyncStatus: () => request<PeopleSyncStatus>('GET', '/api/people-sync'),
  refreshPeopleSync: (full: boolean) => request<PeopleSyncResult>('POST', '/api/people-sync/refresh', { full }),
  peopleDuplicates: () => request<{ groups: DuplicateGroup[]; limit: number }>('GET', '/api/people-review/duplicates'),
  mergeCases: (status: 'open' | 'all') => request<{ cases: MergeCase[] }>('GET', `/api/people-review/merges?${qs({ status })}`),
  reviewMergeCase: (id: string, note: string) => request<{ case: MergeCase }>('POST', `/api/people-review/merges/${encodeURIComponent(id)}/review`, note ? { note } : {}),
  person: (id: string) => request<{ person: Person }>('GET', `/api/people/${encodeURIComponent(id)}`),
  pendingPreloads: (id: string) => request<{ pending: PendingPreload[] }>('GET', `/api/people/${encodeURIComponent(id)}/pending-preloads`),
  createPerson: (body: { displayName: string; email?: string; phone?: string }) => request<{ person: Person }>('POST', '/api/people', body),
  updatePerson: (id: string, body: { displayName?: string; status?: 'active' | 'inactive' }) =>
    request<{ person: Person }>('PATCH', `/api/people/${encodeURIComponent(id)}`, body),
  addContact: (id: string, body: { kind: 'email' | 'phone' | 'address'; value: string }) =>
    request<{ person: Person }>('POST', `/api/people/${encodeURIComponent(id)}/contact-points`, body),
  // Replace a record-only person's email (ADR 0057).
  replaceContact: (id: string, contactId: string, body: { value: string; reason?: string }) =>
    request<{ ok: true }>('PUT', `/api/people/${encodeURIComponent(id)}/contact-points/${encodeURIComponent(contactId)}`, body),
  // Their BRE account's email and claim invite (ADR 0057).
  accountEmail: (id: string) => request<{ status: AccountEmailStatus }>('GET', `/api/people/${encodeURIComponent(id)}/account-email`),
  changeAccountEmail: (id: string, body: { email: string; reason?: string }) =>
    request<AccountEmailChangeResult>('POST', `/api/people/${encodeURIComponent(id)}/account-email`, body),
  // Editing their BRE account (ADR 0066).
  account: (id: string) => request<{ status: AccountIdentities }>('GET', `/api/people/${encodeURIComponent(id)}/account`),
  changeIdentity: (id: string, body: { kind: 'email' | 'phone'; value: string; staffConfirmed?: boolean; reason?: string }) =>
    request<IdentityChangeStarted>('POST', `/api/people/${encodeURIComponent(id)}/account/identities`, body),
  cancelIdentityChange: (id: string, changeId: string) =>
    request<{ cancelled: true }>('POST', `/api/people/${encodeURIComponent(id)}/account/identities/${encodeURIComponent(changeId)}/cancel`, {}),
  removeAccountPhone: (id: string, body: { phone: string; reason?: string }) => request<{ changed: boolean }>('POST', `/api/people/${encodeURIComponent(id)}/account/phone/remove`, body),
  changeAccountName: (id: string, body: { firstName: string; lastName: string }) => request<{ changed: boolean }>('POST', `/api/people/${encodeURIComponent(id)}/account/name`, body),
  signOutAccount: (id: string, body: { reason?: string }) => request<{ sessions: number }>('POST', `/api/people/${encodeURIComponent(id)}/account/sign-out`, body),
  setAccountDisabled: (id: string, body: { disabled: boolean; reason?: string }) =>
    request<{ changed: boolean; disabled: boolean }>('POST', `/api/people/${encodeURIComponent(id)}/account/disabled`, body),
  removeContact: (id: string, contactId: string, body: { reason?: string }) =>
    request<{ ok: true }>('DELETE', `/api/people/${encodeURIComponent(id)}/contact-points/${encodeURIComponent(contactId)}`, body),
  sendAccountInvite: (id: string) => request<{ invite: PreloadInviteStep }>('POST', `/api/people/${encodeURIComponent(id)}/account-invite`),
  personHistory: (id: string) => request<PersonHistory>('GET', `/api/people/${encodeURIComponent(id)}/history`),
  personActivity: (id: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/people/${encodeURIComponent(id)}/activity`),
  lookup: (q: string) => request<HubLookupResponse>('GET', `/api/lookup?q=${encodeURIComponent(q)}`),
  organizationSources: (id: string) => request<OrganizationSourcesResponse>('GET', `/api/organizations/${encodeURIComponent(id)}/sources`),
  organizationLinks: (id: string) => request<{ links: ExternalReferenceHistory[] }>('GET', `/api/organizations/${encodeURIComponent(id)}/links`),
  organizationLinkCandidates: (id: string, source: OrganizationLinkSource, q: string) =>
    request<OrganizationLinkCandidatesResponse>('GET', `/api/organizations/${encodeURIComponent(id)}/link-candidates?${qs({ source, q })}`),
  organizationLinkPreview: (id: string, source: OrganizationLinkSource, sourceId: string) =>
    request<OrganizationLinkPreview>('GET', `/api/organizations/${encodeURIComponent(id)}/link-preview?${qs({ source, sourceId })}`),
  linkOrganization: (id: string, body: { sourceService: OrganizationLinkSource; sourceId: string }) =>
    request<{ organization: OrganizationDetail }>('POST', `/api/organizations/${encodeURIComponent(id)}/links`, body),
  unlinkOrganization: (id: string, refId: string, reason: string) =>
    request<{ organization: OrganizationDetail }>('POST', `/api/organizations/${encodeURIComponent(id)}/links/${encodeURIComponent(refId)}/unlink`, { reason }),
  organizations: (q: string) => request<{ organizations: OrganizationSummary[] }>('GET', `/api/organizations?q=${encodeURIComponent(q)}`),
  organization: (id: string) => request<{ organization: OrganizationDetail }>('GET', `/api/organizations/${encodeURIComponent(id)}`),
  // Organization import, suggested matches and the merge (ADR 0014).
  organizationList: (q: string, filter: OrganizationFilter, offset: number) =>
    request<OrganizationList>('GET', `/api/organization-list?${qs({ q, filter: filter === 'all' ? undefined : filter, offset: offset > 0 ? String(offset) : undefined })}`),
  organizationOrigin: (id: string) => request<{ origin: OrganizationOrigin }>('GET', `/api/organizations/${encodeURIComponent(id)}/origin`),
  organizationImportStatus: () => request<OrgImportStatus & { enabled: boolean }>('GET', '/api/organization-import'),
  refreshOrganizationImport: (full: boolean) => request<OrgImportResult>('POST', '/api/organization-import/refresh', { full }),
  organizationSuggestions: (status: 'open' | 'dismissed' | 'confirmed', organizationId?: string) =>
    request<{ suggestions: MatchSuggestion[]; total: number; limit: number }>('GET', `/api/organization-suggestions?${qs({ status, organizationId })}`),
  dismissOrganizationSuggestion: (id: string, note: string) =>
    request<{ ok: true; openSuggestions: number }>('POST', `/api/organization-suggestions/${encodeURIComponent(id)}/dismiss`, { note: note || undefined }),
  confirmOrganizationSuggestion: (id: string, survivorOrganizationId: string, note: string) =>
    request<{ merge: OrganizationMerge; openSuggestions: number }>('POST', `/api/organization-suggestions/${encodeURIComponent(id)}/confirm`, { survivorOrganizationId, note: note || undefined }),
  organizationSourceReviews: () =>
    request<{ reviews: { organization: { id: string; name: string }; record: OrganizationSourceRecord }[] }>('GET', '/api/organization-source-reviews'),
  reviewOrganizationSource: (referenceId: string, note: string) =>
    request<{ ok: true }>('POST', `/api/organization-source-reviews/${encodeURIComponent(referenceId)}/review`, { note: note || undefined }),
  createOrganization: (body: { name: string; kind?: string }) => request<{ organization: OrganizationDetail }>('POST', '/api/organizations', body),
  addMembership: (body: { personId: string; organizationId: string; relationship: string; title?: string }) =>
    request<{ organization: OrganizationDetail }>('POST', '/api/memberships', body),
  grants: (personId?: string) => request<{ grants: AccessGrant[] }>('GET', `/api/grants${personId ? `?personId=${encodeURIComponent(personId)}` : ''}`),
  grant: (body: { personId: string; capabilities: string[]; validTo?: string; note?: string }) => request<{ grants: AccessGrant[]; added: string[]; skipped: string[] }>('POST', '/api/grants', body),
  grantHolders: (q: string) => request<{ holders: GrantHolder[]; truncated: boolean }>('GET', `/api/grants/people?${qs({ q })}`),
  preloadRewards: (body: PreloadRequest) => request<PreloadReport>('POST', '/api/preload/rewards', body),
  revokeGrant: (id: string) => request<{ grants: AccessGrant[] }>('POST', `/api/grants/${encodeURIComponent(id)}/revoke`),
  // Access by position (ADR 0071).
  positionAccess: () => request<PositionAccessView>('GET', '/api/access/positions'),
  addPositionRule: (body: PositionRuleBody) => request<PositionAccessView>('POST', '/api/access/positions/rules', body),
  removePositionRule: (id: string) => request<PositionAccessView>('POST', `/api/access/positions/rules/${encodeURIComponent(id)}/remove`),
  setPositionAccess: (enabled: boolean) => request<PositionAccessView>('PUT', '/api/access/positions/settings', { enabled }),
  reconcilePositionAccess: () => request<PositionRunResult>('POST', '/api/access/positions/run'),
  myWork: () => request<{ sources: Sourced<PersonWork>[] }>('GET', '/api/me/work'),
  notifications: () => request<{ notifications: HubNotification[]; unreadCount: number }>('GET', '/api/me/notifications'),
  markNotificationRead: (id: string) => request<{ ok: true }>('POST', `/api/me/notifications/${encodeURIComponent(id)}/read`),
  markAllNotificationsRead: () => request<{ ok: true }>('POST', '/api/me/notifications/read-all'),
  notificationSettings: () => request<NotificationSettings>('GET', '/api/me/notification-settings'),
  setNotificationSettings: (body: { emailReminders: boolean }) => request<NotificationSettings>('PUT', '/api/me/notification-settings', body),
  setTheme: (theme: ThemePreference) => request<{ theme: ThemePreference }>('PUT', '/api/me/theme', { theme }),
  projects: (params: { status?: string; q?: string; mine?: boolean }) =>
    request<{ projects: HubProject[] }>('GET', `/api/projects?${new URLSearchParams({ status: params.status ?? 'current', q: params.q ?? '', mine: params.mine ? '1' : '0' })}`),
  project: (id: string) => request<{ project: HubProjectDetail }>('GET', `/api/projects/${encodeURIComponent(id)}`),
  createProject: (body: Record<string, unknown>) => request<{ project: HubProjectDetail }>('POST', '/api/projects', body),
  updateProject: (id: string, body: Record<string, unknown>) => request<{ project: HubProjectDetail }>('PATCH', `/api/projects/${encodeURIComponent(id)}`, body),
  projectActivity: (id: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/projects/${encodeURIComponent(id)}/activity`),
  addProjectMember: (id: string, body: { personId: string; role: string }) => request<{ project: HubProjectDetail }>('POST', `/api/projects/${encodeURIComponent(id)}/members`, body),
  removeProjectMember: (id: string, personId: string) =>
    request<{ project: HubProjectDetail }>('POST', `/api/projects/${encodeURIComponent(id)}/members/${encodeURIComponent(personId)}/remove`),
  linkProjectEvent: (id: string, eventId: string) => request<{ project: HubProjectDetail }>('POST', `/api/projects/${encodeURIComponent(id)}/events`, { eventId }),
  unlinkProjectEvent: (id: string, eventId: string) =>
    request<{ project: HubProjectDetail }>('POST', `/api/projects/${encodeURIComponent(id)}/events/${encodeURIComponent(eventId)}/remove`),
  addMilestone: (id: string, body: { title: string; dueOn?: string | null }) => request<{ project: HubProjectDetail }>('POST', `/api/projects/${encodeURIComponent(id)}/milestones`, body),
  updateMilestone: (id: string, body: { title?: string; dueOn?: string | null; completed?: boolean }) =>
    request<{ project: HubProjectDetail }>('PATCH', `/api/milestones/${encodeURIComponent(id)}`, body),
  eventProjects: (eventId: string) => request<{ projects: HubProject[] }>('GET', `/api/events/${encodeURIComponent(eventId)}/projects`),
  tasks: (params: Record<string, string>) => request<{ tasks: HubTask[] }>('GET', `/api/tasks?${new URLSearchParams(params)}`),
  task: (id: string) => request<{ task: HubTask }>('GET', `/api/tasks/${encodeURIComponent(id)}`),
  createTask: (body: Record<string, unknown>) => request<{ task: HubTask }>('POST', '/api/tasks', body),
  updateTask: (id: string, body: Record<string, unknown>) => request<{ task: HubTask }>('PATCH', `/api/tasks/${encodeURIComponent(id)}`, body),
  taskActivity: (id: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/tasks/${encodeURIComponent(id)}/activity`),
  documents: (targetType: TargetType, targetId: string | null, retired = false) =>
    request<{ documents: HubDocument[] }>('GET', `/api/documents?${new URLSearchParams({ targetType, targetId: targetId ?? '', retired: retired ? '1' : '0' })}`),
  addLinkDocument: (body: { title: string; url: string; classification: string; targetType: TargetType; targetId: string | null }) =>
    request<{ document: HubDocument }>('POST', '/api/documents', { ...body, kind: 'link' }),
  retireDocument: (id: string) => request<{ ok: true }>('POST', `/api/documents/${encodeURIComponent(id)}/retire`),
  document: (id: string) => request<{ document: HubDocument; versions: HubDocumentVersion[] }>('GET', `/api/documents/${encodeURIComponent(id)}`),
  updateDocument: (id: string, body: { title?: string; classification?: string }) => request<{ document: HubDocument }>('PATCH', `/api/documents/${encodeURIComponent(id)}`, body),
  uploadDocumentVersion: (id: string, form: FormData) => upload(`/api/documents/${encodeURIComponent(id)}/versions`, form),
  approvals: (params: { view?: string; status?: string; subjectType?: string; subjectId?: string }) =>
    request<{ approvals: ApprovalSummary[] }>('GET', `/api/approvals?${qs(params)}`),
  approval: (id: string) => request<{ approval: ApprovalDetail }>('GET', `/api/approvals/${encodeURIComponent(id)}`),
  previewApproval: (body: ApprovalSubjectInput) =>
    request<{ subjectType: ApprovalSubjectType; subjectVersion: number | null; title: string; snapshot: ApprovalSnapshot; snapshotHash: string }>('POST', '/api/approvals/preview', body),
  createApproval: (body: ApprovalSubjectInput & { approverPersonIds: string[]; rule: 'all' | 'any'; dueOn?: string | null; note?: string; expectedSnapshotHash?: string }) =>
    request<{ approval: ApprovalDetail }>('POST', '/api/approvals', body),
  decideApproval: (id: string, body: { decision: 'approve' | 'reject'; comment?: string; snapshotHash: string }) =>
    request<{ approval: ApprovalDetail }>('POST', `/api/approvals/${encodeURIComponent(id)}/decisions`, body),
  withdrawApproval: (id: string, reason?: string) => request<{ approval: ApprovalDetail }>('POST', `/api/approvals/${encodeURIComponent(id)}/withdraw`, reason ? { reason } : {}),
  uploadDocument: (form: FormData) => upload('/api/documents/upload', form),
  // Work orders and inspections (ADR 0013).
  // Incidents (ADR 0015).
  incidentOptions: () => request<IncidentReportOptions>('GET', '/api/incidents/options'),
  incidents: (params: IncidentListParams) => request<{ incidents: IncidentSummary[]; truncated: boolean; scope: 'all' | 'mine'; overdueCount: number }>('GET', `/api/incidents?${qs(params)}`),
  incident: (id: string) => request<{ incident: Incident }>('GET', `/api/incidents/${encodeURIComponent(id)}`),
  reportIncident: (body: IncidentReportInput) => request<{ incident: Incident }>('POST', '/api/incidents', body),
  updateIncident: (id: string, body: IncidentChange) => request<{ incident: Incident }>('PATCH', `/api/incidents/${encodeURIComponent(id)}`, body),
  closeIncident: (id: string, body: { rootCause?: string | null; correctiveAction?: string | null; noneReason?: string | null; expectedVersion: number }) =>
    request<{ incident: Incident }>('POST', `/api/incidents/${encodeURIComponent(id)}/close`, body),
  reopenIncident: (id: string, body: { reason: string; expectedVersion: number }) => request<{ incident: Incident }>('POST', `/api/incidents/${encodeURIComponent(id)}/reopen`, body),
  amendIncident: (id: string, body: AmendmentInput) => request<{ incident: Incident }>('POST', `/api/incidents/${encodeURIComponent(id)}/amendments`, body),
  addIncidentNote: (id: string, body: string) => request<{ incident: Incident }>('POST', `/api/incidents/${encodeURIComponent(id)}/notes`, { body }),
  incidentActivity: (id: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/incidents/${encodeURIComponent(id)}/activity`),
  exportIncident: (id: string) => request<{ incident: Incident; exportedAt: string; exportedBy: string | null }>('GET', `/api/incidents/${encodeURIComponent(id)}/export`),
  incidentWorkOrder: (id: string, body: { title: string; description?: string | null; type?: 'repair' | 'damage' | 'other'; priority?: 'low' | 'normal' | 'high' | 'urgent'; dueOn?: string | null }) =>
    request<{ incident: Incident; workOrderId: string }>('POST', `/api/incidents/${encodeURIComponent(id)}/work-orders`, body),
  incidentTask: (id: string, body: { title: string; description?: string | null; assigneePersonId?: string | null; dueOn?: string | null }) =>
    request<{ incident: Incident; taskId: string }>('POST', `/api/incidents/${encodeURIComponent(id)}/tasks`, body),
  uploadIncidentFile: (id: string, file: File) => uploadOwnFile(`/api/incidents/${encodeURIComponent(id)}/files`, file),
  incidentFileUrl: (fileId: string) => `/api/incident-files/${encodeURIComponent(fileId)}/download`,
  personIncidents: (personId: string) => request<{ count: number; incidents: PersonIncidentLink[] }>('GET', `/api/people/${encodeURIComponent(personId)}/incidents`),
  // Lost and found (ADR 0015).
  lostFoundOptions: () => request<IncidentReportOptions>('GET', '/api/lost-found/options'),
  lostFoundSettings: () => request<{ holdDays: number }>('GET', '/api/lost-found/settings'),
  setLostFoundSettings: (holdDays: number) => request<{ holdDays: number }>('PUT', '/api/lost-found/settings', { holdDays }),
  // Settings → Operations (ADR 0026): one read; each value is saved through its own endpoint.
  operationsSettings: () => request<OperationsSettingsView>('GET', '/api/settings/operations'),
  setIncidentRetentionYears: (retentionYears: number) => request<{ years: number }>('PUT', '/api/incidents/retention/settings', { retentionYears }),
  lostFoundStaff: () => request<{ staff: { id: string; displayName: string }[] }>('GET', '/api/lost-found/staff'),
  foundItems: (params: { status?: string; category?: string; facilityId?: string; ready?: string; q?: string; from?: string; to?: string }) =>
    request<{ items: FoundItemSummary[]; truncated: boolean; holdDays: number; today: string }>('GET', `/api/found-items?${qs(params)}`),
  foundItem: (id: string) => request<{ item: FoundItem }>('GET', `/api/found-items/${encodeURIComponent(id)}`),
  logFoundItem: (body: FoundItemInput) => request<{ item: FoundItem }>('POST', '/api/found-items', body),
  updateFoundItem: (id: string, body: Partial<FoundItemInput> & { status?: 'in_storage' | 'claimed'; expectedVersion: number }) =>
    request<{ item: FoundItem }>('PATCH', `/api/found-items/${encodeURIComponent(id)}`, body),
  foundItemActivity: (id: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/found-items/${encodeURIComponent(id)}/activity`),
  claimFoundItem: (id: string, body: ClaimInput) => request<{ item: FoundItem }>('POST', `/api/found-items/${encodeURIComponent(id)}/claims`, body),
  releaseClaim: (claimId: string, body: { verificationNote: string; signatureAck: true }) => request<{ item: FoundItem }>('POST', `/api/found-item-claims/${encodeURIComponent(claimId)}/release`, body),
  declineClaim: (claimId: string, note: string) => request<{ item: FoundItem }>('POST', `/api/found-item-claims/${encodeURIComponent(claimId)}/decline`, { note }),
  disposeFoundItem: (id: string, body: { method: DisposalMethod; note?: string | null }) => request<{ item: FoundItem }>('POST', `/api/found-items/${encodeURIComponent(id)}/dispose`, body),
  uploadFoundItemPhoto: (id: string, file: File) => uploadOwnFile(`/api/found-items/${encodeURIComponent(id)}/photos`, file),
  foundItemPhotoUrl: (fileId: string) => `/api/found-item-files/${encodeURIComponent(fileId)}/download`,
  lostReports: (status: string) => request<{ reports: LostReport[] }>('GET', `/api/lost-reports?${qs({ status })}`),
  lostReport: (id: string) => request<{ report: LostReport; suggestions: FoundItemSummary[] }>('GET', `/api/lost-reports/${encodeURIComponent(id)}`),
  createLostReport: (body: LostReportInput) => request<{ report: LostReport }>('POST', '/api/lost-reports', body),
  matchLostReport: (id: string, foundItemId: string) => request<{ report: LostReport }>('POST', `/api/lost-reports/${encodeURIComponent(id)}/match`, { foundItemId }),
  closeLostReport: (id: string, note: string) => request<{ report: LostReport }>('POST', `/api/lost-reports/${encodeURIComponent(id)}/close`, { note }),
  workOrders: (params: Record<string, string | undefined>) =>
    request<{ today: string; workOrders: WorkOrderSummary[]; truncated: boolean; scope: 'all' | 'mine' }>('GET', `/api/work-orders?${qs(params)}`),
  workOrder: (id: string) => request<{ workOrder: WorkOrder }>('GET', `/api/work-orders/${encodeURIComponent(id)}`),
  createWorkOrder: (body: WorkOrderInput) => request<{ workOrder: WorkOrder }>('POST', '/api/work-orders', body),
  updateWorkOrder: (id: string, body: WorkOrderChange) => request<{ workOrder: WorkOrder }>('PATCH', `/api/work-orders/${encodeURIComponent(id)}`, body),
  addWorkOrderNote: (id: string, body: string) => request<{ ok: true }>('POST', `/api/work-orders/${encodeURIComponent(id)}/notes`, { body }),
  workOrderActivity: (id: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/work-orders/${encodeURIComponent(id)}/activity`),
  setupTemplates: (facilityId: string) => request<{ templates: SetupTemplate[] }>('GET', `/api/facilities/${encodeURIComponent(facilityId)}/setup-templates`),
  createSetupTemplate: (facilityId: string, body: SetupTemplateInput) => request<{ template: SetupTemplate }>('POST', `/api/facilities/${encodeURIComponent(facilityId)}/setup-templates`, body),
  updateSetupTemplate: (id: string, body: Partial<SetupTemplateInput>) => request<{ template: SetupTemplate }>('PATCH', `/api/setup-templates/${encodeURIComponent(id)}`, body),
  retireSetupTemplate: (id: string) => request<{ ok: true }>('POST', `/api/setup-templates/${encodeURIComponent(id)}/retire`),
  eventSetupTemplates: (eventId: string) => request<{ templates: EventSetupTemplate[] }>('GET', `/api/events/${encodeURIComponent(eventId)}/setup-templates`),
  createSetupWorkOrders: (eventId: string, body: { templateIds: string[]; assigneePersonId?: string | null }) =>
    request<{ created: string[]; skipped: string[] }>('POST', `/api/events/${encodeURIComponent(eventId)}/setup-work-orders`, body),
  inspectionTemplates: (retired = false) => request<{ templates: InspectionTemplate[] }>('GET', `/api/inspection-templates${retired ? '?retired=1' : ''}`),
  createInspectionTemplate: (body: InspectionTemplateInput) => request<{ template: InspectionTemplate }>('POST', '/api/inspection-templates', body),
  updateInspectionTemplate: (id: string, body: InspectionTemplateInput) => request<{ template: InspectionTemplate }>('PUT', `/api/inspection-templates/${encodeURIComponent(id)}`, body),
  retireInspectionTemplate: (id: string) => request<{ ok: true }>('POST', `/api/inspection-templates/${encodeURIComponent(id)}/retire`),
  inspectionRuns: (params: { view?: 'due' | 'overdue' | 'history'; templateId?: string; assignee?: string }) =>
    request<{ today: string; runs: InspectionRunSummary[] }>('GET', `/api/inspection-runs?${qs(params)}`),
  inspectionRunActivity: (id: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/inspection-runs/${encodeURIComponent(id)}/activity`),
  inspectionRun: (id: string) => request<{ run: InspectionRun }>('GET', `/api/inspection-runs/${encodeURIComponent(id)}`),
  startInspection: (body: { templateId: string; assetId?: string | null; dueOn?: string | null; assigneePersonId?: string | null }) => request<{ run: InspectionRun }>('POST', '/api/inspection-runs', body),
  recordInspectionResult: (id: string, itemKey: string, body: { outcome: InspectionOutcome; note?: string | null; photoDocumentId?: string | null }) =>
    request<{ run: InspectionRun }>('PUT', `/api/inspection-runs/${encodeURIComponent(id)}/results/${encodeURIComponent(itemKey)}`, body),
  completeInspection: (id: string, notes: string | null) => request<{ run: InspectionRun }>('POST', `/api/inspection-runs/${encodeURIComponent(id)}/complete`, { notes }),
  cancelInspection: (id: string) => request<{ run: InspectionRun }>('POST', `/api/inspection-runs/${encodeURIComponent(id)}/cancel`),
  correctInspection: (id: string) => request<{ run: InspectionRun }>('POST', `/api/inspection-runs/${encodeURIComponent(id)}/correct`),
  runOperations: () => request<OperationsRunResult>('POST', '/api/operations/run', {}),
  // The asset register (ADR 0013).
  assetCategories: () => request<{ categories: AssetCategory[] }>('GET', '/api/asset-categories'),
  createAssetCategory: (name: string) => request<{ category: AssetCategory }>('POST', '/api/asset-categories', { name }),
  renameAssetCategory: (id: string, name: string) => request<{ ok: true }>('PATCH', `/api/asset-categories/${encodeURIComponent(id)}`, { name }),
  retireAssetCategory: (id: string, retired: boolean) => request<{ ok: true }>('POST', `/api/asset-categories/${encodeURIComponent(id)}/retire`, { retired }),
  assets: (params: { q?: string; status?: string; categoryId?: string; facilityId?: string; spaceId?: string }) =>
    request<{ assets: AssetSummary[]; truncated: boolean }>('GET', `/api/assets?${qs(params)}`),
  nextAssetTag: () => request<{ tag: string }>('GET', '/api/assets/next-tag'),
  assetByTag: (tag: string) => request<{ assetId: string }>('GET', `/api/assets/by-tag/${encodeURIComponent(tag)}`),
  asset: (id: string) => request<{ asset: Asset }>('GET', `/api/assets/${encodeURIComponent(id)}`),
  createAsset: (body: AssetInput) => request<{ asset: Asset }>('POST', '/api/assets', body),
  updateAsset: (id: string, body: Partial<AssetInput>) => request<{ asset: Asset }>('PATCH', `/api/assets/${encodeURIComponent(id)}`, body),
  assetActivity: (id: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/assets/${encodeURIComponent(id)}/activity`),
  assetCheckouts: (id: string) => request<{ checkouts: AssetCheckout[] }>('GET', `/api/assets/${encodeURIComponent(id)}/checkouts`),
  openCheckouts: (params: { view?: 'open' | 'overdue'; personId?: string; eventId?: string }) =>
    request<{ today: string; checkouts: AssetCheckout[] }>('GET', `/api/asset-checkouts?${qs(params)}`),
  checkOutAsset: (id: string, body: { personId?: string | null; eventId?: string | null; dueBackOn?: string | null; note?: string | null }) =>
    request<{ asset: Asset }>('POST', `/api/assets/${encodeURIComponent(id)}/checkout`, body),
  returnAsset: (id: string, body: { condition: AssetCondition; note?: string | null; status: 'in_service' | 'out_for_repair' | 'lost' }) =>
    request<{ asset: Asset }>('POST', `/api/assets/${encodeURIComponent(id)}/return`, body),
  replacementPlan: () => request<{ years: ReplacementYear[]; assets: Asset[]; withoutYear: number }>('GET', '/api/assets/planning/replacement'),
  warrantyList: () => request<{ today: string; from: string; to: string; assets: Asset[] }>('GET', '/api/assets/planning/warranty'),
  maintenanceSchedules: (params: { assetId?: string; categoryId?: string }) =>
    request<{ schedules: MaintenanceSchedule[]; inherited: boolean }>('GET', `/api/maintenance-schedules?${qs(params)}`),
  createMaintenanceSchedule: (body: MaintenanceScheduleInput) => request<{ schedule: MaintenanceSchedule }>('POST', '/api/maintenance-schedules', body),
  retireMaintenanceSchedule: (id: string) => request<{ ok: true }>('POST', `/api/maintenance-schedules/${encodeURIComponent(id)}/retire`),
  // Facilities and spaces (ADR 0013).
  facilities: (all = false) => request<{ facilities: FacilitySummary[] }>('GET', `/api/facilities${all ? '?status=all' : ''}`),
  facility: (id: string) => request<{ facility: Facility }>('GET', `/api/facilities/${encodeURIComponent(id)}`),
  createFacility: (body: FacilityInput) => request<{ facility: Facility }>('POST', '/api/facilities', body),
  updateFacility: (id: string, body: Partial<FacilityInput>) => request<{ facility: Facility }>('PATCH', `/api/facilities/${encodeURIComponent(id)}`, body),
  facilityActivity: (id: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/facilities/${encodeURIComponent(id)}/activity`),
  facilityEvents: (id: string) => request<{ events: PlaceEvent[] }>('GET', `/api/facilities/${encodeURIComponent(id)}/events`),
  spaces: (facilityId?: string) => request<{ spaces: SpaceOption[] }>('GET', `/api/spaces?${qs({ facilityId })}`),
  space: (id: string) => request<{ space: Space }>('GET', `/api/spaces/${encodeURIComponent(id)}`),
  createSpace: (facilityId: string, body: SpaceInput) => request<{ space: Space }>('POST', `/api/facilities/${encodeURIComponent(facilityId)}/spaces`, body),
  updateSpace: (id: string, body: Partial<SpaceInput>) => request<{ space: Space }>('PATCH', `/api/spaces/${encodeURIComponent(id)}`, body),
  spaceActivity: (id: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/spaces/${encodeURIComponent(id)}/activity`),
  spaceEvents: (id: string) => request<{ events: PlaceEvent[] }>('GET', `/api/spaces/${encodeURIComponent(id)}/events`),
  addConfiguration: (spaceId: string, body: { name: string; capacity: number; notes?: string | null }) =>
    request<{ space: Space }>('POST', `/api/spaces/${encodeURIComponent(spaceId)}/configurations`, body),
  updateConfiguration: (id: string, body: { name?: string; capacity?: number; notes?: string | null }) =>
    request<{ space: Space }>('PATCH', `/api/space-configurations/${encodeURIComponent(id)}`, body),
  retireConfiguration: (id: string) => request<{ space: Space }>('POST', `/api/space-configurations/${encodeURIComponent(id)}/retire`),
  linkBookingRoom: (spaceId: string, body: { sourceId: string; label: string }) =>
    request<{ space: Space }>('POST', `/api/spaces/${encodeURIComponent(spaceId)}/source-links`, { sourceService: 'booking', entityType: 'room', ...body }),
  unlinkBookingRoom: (spaceId: string, linkId: string) =>
    request<{ space: Space }>('POST', `/api/spaces/${encodeURIComponent(spaceId)}/source-links/${encodeURIComponent(linkId)}/unlink`),
  eventPlaces: (eventId: string) => request<{ places: EventPlace[] }>('GET', `/api/events/${encodeURIComponent(eventId)}/places`),
  addEventPlace: (eventId: string, body: { facilityId: string; spaceId?: string | null }) =>
    request<{ places: EventPlace[] }>('POST', `/api/events/${encodeURIComponent(eventId)}/places`, body),
  removeEventPlace: (eventId: string, placeId: string) =>
    request<{ places: EventPlace[] }>('POST', `/api/events/${encodeURIComponent(eventId)}/places/${encodeURIComponent(placeId)}/remove`),
  search: (q: string) =>
    request<{ query: string; groups: { type: SearchGroupType; hits: { id: string; title: string; detail: string | null; path: string }[] }[] }>(
      'GET',
      `/api/search?q=${encodeURIComponent(q)}`,
    ),
  command: async <R,>(personId: string, body: { type: string; commandId: string; payload: unknown; expected?: unknown }): Promise<CommandOutcome<R>> => {
    const response = await fetch(`/api/people/${encodeURIComponent(personId)}/commands`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify(body),
    })
    if (response.status === 401) reauthenticate()
    const payload = (await response.json().catch(() => null)) as
      | (CommandOutcome<R> & { error?: { code?: string; message?: string } })
      | { error?: { code?: string; message?: string } }
      | null
    if (payload && 'outcome' in payload) return payload
    throw new ApiError(response.status, payload?.error?.code ?? 'ERROR', payload?.error?.message ?? `Request failed (${response.status})`)
  },
  health: () => request<{ sources: SourceHealth[] }>('GET', '/api/system/health'),
  events: (params: { q?: string; from?: string; to?: string }) =>
    request<{ events: HubEvent[] }>('GET', `/api/events?${new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][])}`),
  event: (id: string) => request<{ event: HubEvent; view: Event360 }>('GET', `/api/events/${encodeURIComponent(id)}`),
  eventFinance: (id: string) => request<EventFinance>('GET', `/api/events/${encodeURIComponent(id)}/finance`),
  eventApprovals: (id: string) => request<{ approvals: ApprovalSummary[] }>('GET', `/api/events/${encodeURIComponent(id)}/approvals`),
  eventSettlements: (id: string) => request<{ settlements: EventSettlement[] }>('GET', `/api/events/${encodeURIComponent(id)}/settlements`),
  createSettlement: (eventId: string, body: { stage: SettlementStage; note?: string }) =>
    request<{ settlement: EventSettlement }>('POST', `/api/events/${encodeURIComponent(eventId)}/settlements`, body),
  updateSettlement: (id: string, body: { stage?: SettlementStage; note?: string | null }) => request<{ settlement: EventSettlement }>('PATCH', `/api/settlements/${encodeURIComponent(id)}`, body),
  addSettlementLine: (id: string, body: SettlementLineInput) => request<{ settlement: EventSettlement }>('POST', `/api/settlements/${encodeURIComponent(id)}/lines`, body),
  updateSettlementLine: (id: string, lineId: string, body: Partial<SettlementLineInput>) =>
    request<{ settlement: EventSettlement }>('PATCH', `/api/settlements/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`, body),
  removeSettlementLine: (id: string, lineId: string) => request<{ settlement: EventSettlement }>('DELETE', `/api/settlements/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}`),
  pullSettlementSources: (id: string) => request<{ settlement: EventSettlement }>('POST', `/api/settlements/${encodeURIComponent(id)}/sources`),
  reviseSettlement: (id: string) => request<{ settlement: EventSettlement }>('POST', `/api/settlements/${encodeURIComponent(id)}/revise`),
  settlementActivity: (id: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/settlements/${encodeURIComponent(id)}/activity`),
  settlement: (id: string) =>
    request<{ settlement: EventSettlement; event: { id: string; name: string; startsOn: string; endsOn: string } | null }>('GET', `/api/settlements/${encodeURIComponent(id)}`),
  settlements: (params: { phase?: string; cursor?: string | null }) =>
    request<{ settlements: SettlementListItem[]; nextCursor: string | null }>(
      'GET',
      `/api/settlements?${new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][])}`,
    ),
  eventNight: (eventId: string) => request<NightMoney>('GET', `/api/events/${encodeURIComponent(eventId)}/night`),
  settlementCompare: (id: string) => request<{ asOf: string; comparison: SourceComparison }>('GET', `/api/settlements/${encodeURIComponent(id)}/compare`),
  setSettlementDeal: (id: string, terms: Omit<SettlementDealTerms, 'organizationId'> & { organizationId?: string | null }) =>
    request<{ settlement: EventSettlement }>('PUT', `/api/settlements/${encodeURIComponent(id)}/deal`, terms),
  removeSettlementDeal: (id: string) => request<{ settlement: EventSettlement }>('DELETE', `/api/settlements/${encodeURIComponent(id)}/deal`),
  // ADR 0040: the parties of a deal (a lineup, a co-promoter).
  addSettlementParty: (id: string, terms: SettlementPartyInput) => request<{ settlement: EventSettlement; partyKey: string }>('POST', `/api/settlements/${encodeURIComponent(id)}/parties`, terms),
  updateSettlementParty: (id: string, key: string, terms: SettlementPartyInput) =>
    request<{ settlement: EventSettlement }>('PUT', `/api/settlements/${encodeURIComponent(id)}/parties/${encodeURIComponent(key)}`, terms),
  removeSettlementParty: (id: string, key: string) => request<{ settlement: EventSettlement }>('DELETE', `/api/settlements/${encodeURIComponent(id)}/parties/${encodeURIComponent(key)}`),
  moveSettlementParty: (id: string, key: string, direction: 'up' | 'down') =>
    request<{ settlement: EventSettlement }>('POST', `/api/settlements/${encodeURIComponent(id)}/parties/${encodeURIComponent(key)}/move`, { direction }),
  recordSettlementStep: (id: string, body: SettlementStepInput) => request<{ settlement: EventSettlement }>('POST', `/api/settlements/${encodeURIComponent(id)}/steps`, body),
  expenseCatalog: () => request<ExpenseCatalog>('GET', '/api/settlement-expense-categories'),
  createEvent: (body: { name: string; startsOn: string; endsOn: string; notes?: string }) => request<{ event: HubEvent }>('POST', '/api/events', body),
  updateEvent: (id: string, body: Partial<Pick<HubEvent, 'name' | 'startsOn' | 'endsOn' | 'status' | 'notes' | 'kind' | 'serviceStartsLocal' | 'serviceEndsLocal'>> & { review?: null; expectedVersion?: number }) =>
    request<{ event: HubEvent }>('PATCH', `/api/events/${encodeURIComponent(id)}`, body),
  linkSource: (id: string, body: { sourceService: string; entityType: string; sourceId: string; titleSnapshot: string; startsAtSnapshot: string | null }) =>
    request<{ event: HubEvent }>('POST', `/api/events/${encodeURIComponent(id)}/links`, body),
  unlinkSource: (id: string, linkId: string) => request<{ event: HubEvent }>('POST', `/api/events/${encodeURIComponent(id)}/links/${encodeURIComponent(linkId)}/unlink`),
  eventActivity: (id: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/events/${encodeURIComponent(id)}/activity`),
  sourceEvents: (from: string, to: string) =>
    request<{ sources: Sourced<SourceEventList>[]; links: { sourceService: string; entityType: string; sourceId: string; eventId: string; eventName: string }[] }>(
      'GET',
      `/api/source-events?from=${from}&to=${to}`,
    ),
  cases: (params: { queue?: string; status?: string; assignee?: string; q?: string; requesterPersonId?: string }) =>
    request<{ cases: SupportCaseSummary[] }>('GET', `/api/cases?${new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][])}`),
  supportCase: (id: string) => request<{ case: SupportCase }>('GET', `/api/cases/${encodeURIComponent(id)}`),
  createCase: (body: {
    subject: string
    channel: CaseChannel
    queue: CaseQueue
    priority?: CasePriority
    requesterPersonId?: string | null
    organizationId?: string | null
    assigneePersonId?: string | null
    message?: string
  }) => request<{ case: SupportCase }>('POST', '/api/cases', body),
  updateCase: (id: string, body: CaseUpdate) => request<{ case: SupportCase }>('PATCH', `/api/cases/${encodeURIComponent(id)}`, body),
  addCaseEntry: (id: string, body: { kind: 'internal_note' | 'customer_message'; body: string }) =>
    request<{ case: SupportCase }>('POST', `/api/cases/${encodeURIComponent(id)}/entries`, body),
  addCaseLink: (id: string, body: { targetType: CaseLink['targetType']; targetId: string; label?: string }) =>
    request<{ case: SupportCase }>('POST', `/api/cases/${encodeURIComponent(id)}/links`, body),
  removeCaseLink: (id: string, linkId: string) => request<{ case: SupportCase }>('DELETE', `/api/cases/${encodeURIComponent(id)}/links/${encodeURIComponent(linkId)}`),
  personCases: (id: string) => request<{ cases: SupportCaseSummary[] }>('GET', `/api/people/${encodeURIComponent(id)}/cases`),
  financeOverview: () => request<{ apps: FinanceOverviewApp[]; environment: string; today: string }>('GET', '/api/finance/overview'),
  financeExceptions: (params: { status?: string; app?: string; kind?: string; severity?: string }) =>
    request<{ exceptions: FinanceException[]; counts: { open: number; acknowledged: number; cleared: number } }>('GET', `/api/finance/exceptions?${qs(params)}`),
  financeException: (id: string) =>
    request<{ exception: FinanceException; sync: FinanceSync | null; activity: ActivityEntry[] }>('GET', `/api/finance/exceptions/${encodeURIComponent(id)}`),
  acknowledgeFinanceException: (id: string, note: string) =>
    request<{ exception: FinanceException }>('POST', `/api/finance/exceptions/${encodeURIComponent(id)}/acknowledge`, { note }),
  financePostings: (params: Record<string, string | undefined>) =>
    request<{ postings: FinancePostingRow[]; total: number; limit: number; offset: number; streams: { app: string; stream: string }[] }>('GET', `/api/finance/postings?${qs(params)}`),
  financePayouts: (params: Record<string, string | undefined>) => request<{ payouts: FinancePayoutRow[] }>('GET', `/api/finance/payouts?${qs(params)}`),
  financeAccounts: () => request<{ environment: string; accounts: FinanceAccountEntry[] }>('GET', '/api/finance/accounts'),
  saveFinanceAccount: (app: string, body: { stripeAccount: string | null; stripeMode: 'live' | 'test' | null; qboRealmId: string | null; note?: string | null }) =>
    request<{ account: FinanceAccount }>('PUT', `/api/finance/accounts/${encodeURIComponent(app)}`, body),
  financeAccountActivity: (app: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/finance/accounts/${encodeURIComponent(app)}/activity`),
  refreshFinance: (app?: string) => request<{ results: FinanceRefreshResult[] }>('POST', '/api/finance/refresh', app ? { app } : {}),
  organizationActivity: (id: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/organizations/${encodeURIComponent(id)}/activity`),
  notes: (subjectType: NoteSubjectType, subjectId: string, retired = false) =>
    request<{ notes: Note[] }>('GET', `/api/notes?${qs({ subjectType, subjectId, retired: retired ? '1' : undefined })}`),
  addNote: (body: { subjectType: NoteSubjectType; subjectId: string; body: string; visibility: 'internal' | 'confidential'; pinned: boolean }) =>
    request<{ note: Note }>('POST', '/api/notes', body),
  editNote: (id: string, body: { body: string; visibility?: 'internal' | 'confidential'; expectedVersion: number }) =>
    request<{ note: Note }>('PATCH', `/api/notes/${encodeURIComponent(id)}`, body),
  pinNote: (id: string, pinned: boolean) => request<{ note: Note }>('POST', `/api/notes/${encodeURIComponent(id)}/pin`, { pinned }),
  retireNote: (id: string, reason: string) => request<{ ok: true }>('POST', `/api/notes/${encodeURIComponent(id)}/retire`, { reason }),
  noteHistory: (id: string) => request<{ note: Note; versions: NoteVersion[] }>('GET', `/api/notes/${encodeURIComponent(id)}/history`),
  organizationRelationships: (id: string) => request<{ relationships: OrganizationRelationship[] }>('GET', `/api/organizations/${encodeURIComponent(id)}/relationships`),
  proposeRelationship: (
    organizationId: string,
    body: { type: string; relatedOrganizationId?: string; effectiveFrom?: string; effectiveTo?: string; note?: string; evidenceReferenceId?: string; confirmNow?: boolean },
  ) => request<{ relationship: OrganizationRelationship }>('POST', `/api/organizations/${encodeURIComponent(organizationId)}/relationships`, body),
  relationships: (status = 'proposed') => request<{ relationships: OrganizationRelationship[] }>('GET', `/api/relationships?${qs({ status })}`),
  confirmRelationship: (id: string) => request<{ relationship: OrganizationRelationship }>('POST', `/api/relationships/${encodeURIComponent(id)}/confirm`, {}),
  rejectRelationship: (id: string, reason: string) => request<{ relationship: OrganizationRelationship }>('POST', `/api/relationships/${encodeURIComponent(id)}/reject`, { reason }),
  endRelationship: (id: string, body: { effectiveTo?: string; reason?: string }) =>
    request<{ relationship: OrganizationRelationship }>('POST', `/api/relationships/${encodeURIComponent(id)}/end`, body),
  requirements: (params: { view?: string; subjectType?: string; subjectId?: string; mine?: boolean }) =>
    request<{ requirements: HubRequirement[]; today: string }>('GET', `/api/requirements?${qs({ view: params.view, subjectType: params.subjectType, subjectId: params.subjectId, mine: params.mine ? '1' : undefined })}`),
  requirement: (id: string) => request<{ requirement: HubRequirementDetail }>('GET', `/api/requirements/${encodeURIComponent(id)}`),
  createRequirement: (body: {
    subjectType: RequirementSubjectType
    subjectId: string
    title: string
    description?: string | null
    kind: RequirementKind
    dueOn?: string | null
    ownerPersonId?: string | null
    renewsEveryMonths?: number | null
  }) => request<{ requirement: HubRequirementDetail }>('POST', '/api/requirements', body),
  updateRequirement: (id: string, body: { expectedVersion: number; title?: string; description?: string | null; dueOn?: string | null; ownerPersonId?: string | null; renewsEveryMonths?: number | null }) =>
    request<{ requirement: HubRequirementDetail }>('PATCH', `/api/requirements/${encodeURIComponent(id)}`, body),
  satisfyRequirement: (id: string, body: { evidence: SatisfyEvidence; note?: string; validUntil?: string }) =>
    request<{ requirement: HubRequirementDetail }>('POST', `/api/requirements/${encodeURIComponent(id)}/satisfy`, body),
  waiveRequirement: (id: string, body: { reason: string; validUntil?: string }) => request<{ requirement: HubRequirementDetail }>('POST', `/api/requirements/${encodeURIComponent(id)}/waive`, body),
  requirementNotApplicable: (id: string, reason: string) => request<{ requirement: HubRequirementDetail }>('POST', `/api/requirements/${encodeURIComponent(id)}/not-applicable`, { reason }),
  reopenRequirement: (id: string, reason: string) => request<{ requirement: HubRequirementDetail }>('POST', `/api/requirements/${encodeURIComponent(id)}/reopen`, { reason }),
  checklists: (retired = false) => request<{ checklists: HubChecklist[] }>('GET', `/api/checklists${retired ? '?retired=1' : ''}`),
  checklist: (id: string) => request<{ checklist: HubChecklist }>('GET', `/api/checklists/${encodeURIComponent(id)}`),
  createChecklist: (body: { name: string; description?: string | null; items: ChecklistItem[] }) => request<{ checklist: HubChecklist }>('POST', '/api/checklists', body),
  editChecklist: (id: string, body: { expectedVersion: number; name: string; description?: string | null; items: ChecklistItem[] }) =>
    request<{ checklist: HubChecklist }>('PUT', `/api/checklists/${encodeURIComponent(id)}`, body),
  retireChecklist: (id: string) => request<{ checklist: HubChecklist }>('POST', `/api/checklists/${encodeURIComponent(id)}/retire`, {}),
  stampChecklist: (id: string, body: { subjectType: RequirementSubjectType; subjectId: string; ownerPersonId?: string | null; anchorOn?: string; again?: boolean }) =>
    request<{ stampId: string; requirementIds: string[]; version: number }>('POST', `/api/checklists/${encodeURIComponent(id)}/stamp`, body),
  reminders: (itemType: ReminderItemType, itemId: string) => request<ReminderView>('GET', `/api/reminders?${qs({ itemType, itemId })}`),
  defaultReminderRules: (itemType: ReminderItemType) => request<{ rules: ReminderRule[] }>('GET', `/api/reminders?${qs({ itemType })}`),
  addReminderRule: (body: { itemType: ReminderItemType; itemId?: string; anchor?: 'due' | 'expiry'; offsetDays: number; action: 'remind' | 'escalate'; escalateToPersonId?: string; email?: boolean }) =>
    request<{ rule: ReminderRule }>('POST', '/api/reminder-rules', body),
  retireReminderRule: (id: string) => request<{ ok: true }>('POST', `/api/reminder-rules/${encodeURIComponent(id)}/retire`, {}),
  signOut: () => request<{ redirectTo: string }>('POST', '/auth/logout'),
  formTemplates: () => request<{ templates: FormTemplateSummary[] }>('GET', '/api/form-templates'),
  forms: (archived = false) => request<{ forms: FormSummary[] }>('GET', `/api/forms${archived ? '?archived=1' : ''}`),
  form: (id: string) => request<{ form: FormDetail }>('GET', `/api/forms/${encodeURIComponent(id)}`),
  formActivity: (id: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/forms/${encodeURIComponent(id)}/activity`),
  formVersion: (id: string, version: number) => request<{ definition: FormDefinition }>('GET', `/api/forms/${encodeURIComponent(id)}/versions/${version}`),
  createForm: (body: { name: string; slug: string; templateKey?: string }) => request<{ form: FormDetail }>('POST', '/api/forms', body),
  saveFormDraft: (id: string, body: { definition: FormDefinition; expectedDraftUpdatedAt: string }) => request<{ form: FormDetail }>('PUT', `/api/forms/${encodeURIComponent(id)}/draft`, body),
  saveFormSettings: (id: string, body: FormSettingsChange) => request<{ form: FormDetail }>('PATCH', `/api/forms/${encodeURIComponent(id)}/settings`, body),
  formAction: (id: string, action: 'publish' | 'close' | 'reopen' | 'archive') => request<{ form: FormDetail }>('POST', `/api/forms/${encodeURIComponent(id)}/${action}`, {}),
  formSubmissions: (params: SubmissionListParams) =>
    request<{ submissions: SubmissionSummary[]; total: number; offset: number; limit: number }>(
      'GET',
      `/api/form-submissions?${qs({ formId: params.formId, status: params.status || undefined, personId: params.personId, from: params.from, to: params.to, offset: params.offset ? String(params.offset) : undefined })}`,
    ),
  formSubmission: (id: string) => request<{ submission: SubmissionDetail }>('GET', `/api/form-submissions/${encodeURIComponent(id)}`),
  formSubmissionActivity: (id: string) => request<{ activity: ActivityEntry[] }>('GET', `/api/form-submissions/${encodeURIComponent(id)}/activity`),
  updateFormSubmission: (id: string, body: { status?: SubmissionStatus; assigneePersonId?: string | null; reason?: string }) =>
    request<{ submission: SubmissionDetail }>('PATCH', `/api/form-submissions/${encodeURIComponent(id)}`, body),
  addFormSubmissionNote: (id: string, body: string) => request<{ submission: SubmissionDetail }>('POST', `/api/form-submissions/${encodeURIComponent(id)}/notes`, { body }),
  holdFormSubmission: (id: string, hold: boolean, reason: string) => request<{ submission: SubmissionDetail }>('POST', `/api/form-submissions/${encodeURIComponent(id)}/hold`, { hold, reason }),
  linkFormSubmission: (id: string, personId: string) => request<{ submission: SubmissionDetail }>('POST', `/api/form-submissions/${encodeURIComponent(id)}/link`, { personId }),
  acceptFormOrganization: (id: string, body: { organizationId?: string; createName?: string; note?: string }) =>
    request<{ submission: SubmissionDetail }>('POST', `/api/form-submissions/${encodeURIComponent(id)}/organization`, body),
  dismissFormOrganization: (id: string, reason: string) => request<{ submission: SubmissionDetail }>('POST', `/api/form-submissions/${encodeURIComponent(id)}/organization/dismiss`, { reason }),
  acceptFormFile: (id: string, fileId: string, body: { title: string; classification: 'public' | 'internal' | 'confidential' }) =>
    request<{ submission: SubmissionDetail; documentId: string }>('POST', `/api/form-submissions/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}/accept`, body),
  rejectFormFile: (id: string, fileId: string, reason: string) =>
    request<{ submission: SubmissionDetail }>('POST', `/api/form-submissions/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}/reject`, { reason }),
  formFileDownloadUrl: (id: string, fileId: string) => `/api/form-submissions/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}/download`,
  formSubmissionsCsvUrl: (formId: string, params: { status?: string; from?: string; to?: string }) => `/api/forms/${encodeURIComponent(formId)}/submissions.csv?${qs(params)}`,
}
